import { Box, Checkbox, FormControlLabel, Grid, Theme, Typography, useTheme } from '@mui/material';
import React from 'react';
import { ApplicationProps, RelatedParty } from '../../redux-store/types/api-types';
import { KeyValue } from '../ApplicationDetails/personalDetails';
import { SubHeading } from '../investors/components';
import { CpUboCodesMaster } from '../../utils/constant';
import dayjs from '../../utils/dayjs';

const hasType = (party: RelatedParty, type: string): boolean => {
    return party.relatedPartyTypes?.some((t) => t.type === type && t.isActive) ?? false;
};

const formatDob = (dob: string | undefined): string => {
    if (!dob) return '-';
    const parsed = dayjs(dob);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : dob;
};

const formatAddress = (a1?: string, a2?: string, a3?: string, city?: string, state?: string, country?: string, pincode?: string): string => {
    return [a1, a2, a3, city, state, country, pincode].filter(Boolean).join(', ') || '-';
};

const typeLabels: Record<string, string> = {
    director: 'Director',
    promoter: 'Promoter',
    karta: 'Karta',
    trustee: 'Trustee',
    partner: 'Partner',
    courtAppointed: 'Court Appointed Official Proprietor',
    beneficiary: 'Beneficiary',
    authorisedsignatory: 'Authorized Signatory',
    ubo: 'Beneficial Owner (UBO)',
    powerOfAttorneyHolder: 'Power of Attorney Holder',
    others: 'Others',
};

const allTypeKeys = Object.keys(typeLabels);

function PartyCard({ party, index }: { party: RelatedParty; index: number }): JSX.Element {
    return (
        <Box>
            {/* Type checkboxes - disabled, showing state from API */}
            <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                {allTypeKeys.map((typeKey) => (
                    <FormControlLabel
                        key={typeKey}
                        control={<Checkbox checked={hasType(party, typeKey)} disabled size="small" />}
                        label={typeLabels[typeKey]}
                        sx={{ m: 0 }}
                    />
                ))}
            </Box>
            {party.otherRelatedPartyType && (
                <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>
                    Others: {party.otherRelatedPartyType}
                </Typography>
            )}

            {/* Name */}
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                {party.name || `Related Party ${index + 1}`}
            </Typography>

            {/* All details under one heading */}
            <SubHeading sx={{ mb: 2 }}>Related Party Details</SubHeading>
            <Grid container rowSpacing={1} columnSpacing={3}>
                <KeyValue title="PAN" description={party.pan?.toUpperCase() || '-'} />
                <KeyValue title="Date of Birth" description={formatDob(party.dob)} />
                <KeyValue title="Father/Spouse Name" description={party.fatherOrSpouseName || '-'} />
                <KeyValue title="Email" description={party.email || '-'} />
                <KeyValue title="Mobile" description={party.mobile || '-'} />
                <KeyValue title="Identity Type" description={party.identificationType || '-'} />
                <KeyValue title="ID Number" description={party.identificationNumber || '-'} />
                <KeyValue title="DIN" description={party.din || '-'} />
                <KeyValue title="Designation" description={party.designation || '-'} />
                <KeyValue title="Gender" description={party.gender || '-'} />
                <KeyValue title="Occupation" description={party.occupation || '-'} />
                <KeyValue title="Nationality" description={party.nationality || '-'} />
                <KeyValue title="Country of Tax Residency" description={party.countryOfTaxResidency || '-'} />
                <KeyValue title="UBO %" description={party.percentageOfBeneficialInterest ? `${party.percentageOfBeneficialInterest}%` : '-'} />
                <KeyValue title="UBO Code" description={party.cpUboCode ? `${party.cpUboCode} - ${CpUboCodesMaster[party.cpUboCode] || party.cpUboCode}` : '-'} />
                <KeyValue title="Place of Birth" description={party.placeAndCountryOfBirth || '-'} />
                <KeyValue title="CKYC Number" description={party.ckycNumber || '-'} />
                <KeyValue title="Correspondence Address" description={formatAddress(party.correspondenceAddress1, party.correspondenceAddress2, party.correspondenceAddress3, party.correspondenceCity, party.correspondenceState, party.correspondenceCountry, party.correspondencePincode)} />
                <KeyValue title="Correspondence Address Type" description={party.correspondenceAddressType || '-'} />
                <KeyValue title="Permanent Address" description={party.permanentAddressSameAsCorrespondence === 'Y' ? formatAddress(party.correspondenceAddress1, party.correspondenceAddress2, party.correspondenceAddress3, party.correspondenceCity, party.correspondenceState, party.correspondenceCountry, party.correspondencePincode) : formatAddress(party.permanentAddress1, party.permanentAddress2, party.permanentAddress3, party.permanentCity, party.permanentState, party.permanentCountry, party.permanentPincode)} />
                <KeyValue title="Permanent Address Type" description={party.permanentAddressSameAsCorrespondence === 'Y' ? (party.correspondenceAddressType || '-') : (party.permanentAddressType || '-')} />
            </Grid>
        </Box>
    );
}

export default function RelatedPartiesDetails({ application }: { application: ApplicationProps }): JSX.Element {
    const theme = useTheme();
    const { relatedParties = [], groupSignatoriesThreshold = 0 } = application || {};
    const activeParties = relatedParties.filter((p) => p.relatedPartyTypes?.some((t) => t.isActive));

    return (
        <Box
            sx={(theme: Theme) => ({
                bgcolor: 'background.paper',
                boxShadow: `0px 4px 12px ${theme.palette.common.black}1A`,
                borderRadius: '10px',
                border: `1px solid ${theme.palette.grey[100]}`,
                padding: '25px',
            })}
            mt={5}
        >
            <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '20px' })}>
                Related Parties
            </Typography>

            {activeParties.length === 0 && (
                <Typography sx={{ fontSize: 16, fontWeight: 500, color: 'text.secondary', textAlign: 'center', my: 3 }}>
                    No Related Parties
                </Typography>
            )}

            <Typography variant='body_500' sx={{ color: 'text.secondary', my: 3 }}>
                Total No. of required authorised signatories: <Typography component="span" variant='body_500' sx={{ color: 'text.primary', my: 3 }}>{groupSignatoriesThreshold}</Typography>
            </Typography>

            {activeParties.map((party, index) => (
                <React.Fragment key={index}>
                    <PartyCard party={party} index={index} />
                    {index < activeParties.length - 1 && (
                        <Box sx={(t) => ({ borderBottom: `1px solid ${t.palette.grey[200]}`, my: 3 })} />
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
}
